import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';

// Api
import client from '../../api/client';

// Constants
import {
  CompanyName,
  FULL_BUSINESS_URL,
  KeywordsPages,
} from '../../utils/constants/Constants';
import {
  MAILING_LIST_VALIDATE_EMAIL_API,
  MAILING_LIST_RESEND_VALIDATE_EMAIL_API,
} from '../../utils/constants/ApiRoutes';
import {
  MAILING_LIST_CONFIRM_EMAIL_PAGE_URL,
  HOME_PAGE_URL,
} from '../../utils/constants/Routes';

// Data
import {
  mailingListValidationAdditionalMeta,
  mailingListValidationStructuredData,
} from '../../utils/data/MetaData';

// Components
import Navbar from '../../components/nav/Navbar';
import { HelmetItem } from '../../components/utils/HelmetItem';
import LoadingSpinner from '../../components/utils/LoadingSpinner';

// Utils
import { buildBreadcrumbLd } from '../../utils/seo/breadcrumbs';
import FAQSchema from '../../utils/seo/FAQSchema';
import { mailingListFaqItems } from '../../utils/data/faqSchemas';

function MailingValidationPage() {
  const { userId, verificationTokenId, uniqueString } = useParams();
  const [status, setStatus] = useState('loading'); // 'loading', 'success', 'error'
  const [resendStatus, setResendStatus] = useState(null); // null, 'loading', 'success', 'error'

  const breadcrumbLd = buildBreadcrumbLd({
    baseUrl: FULL_BUSINESS_URL,
    items: [
      { label: 'Home', href: HOME_PAGE_URL },
      { label: 'Confirm Email', href: MAILING_LIST_CONFIRM_EMAIL_PAGE_URL },
    ],
  });

  useEffect(() => {
    const verifyEmail = async () => {
      if (!userId || !verificationTokenId || !uniqueString) {
        setStatus('error');
        return;
      }

      client
        .patch(
          `${MAILING_LIST_VALIDATE_EMAIL_API}/${userId}/${verificationTokenId}/${uniqueString}`,
          null,
          false
        )
        .then((res) => {
          setStatus(res.status === 'success' ? 'success' : 'error');
        })
        .catch(() => {
          setStatus('error');
        });
    };

    verifyEmail();
  }, [userId, verificationTokenId, uniqueString]);

  const handleResendVerification = () => {
    setResendStatus('loading');

    client
      .post(`${MAILING_LIST_RESEND_VALIDATE_EMAIL_API}/${userId}`, null, false)
      .then(() => setResendStatus('success'))
      .catch(() => setResendStatus('error'));
  };

  return (
    <>
      {/* Tab Data */}
      <HelmetItem
        PageName="Confirm Email – Mailing List"
        desc={`Verify your email to finish joining the ${CompanyName} mailing list. Receive session openings, tour plans, and essential updates from your Muscle Goddess.`}
        keywords={KeywordsPages}
        additionalMeta={mailingListValidationAdditionalMeta}
        structuredData={[mailingListValidationStructuredData, breadcrumbLd]}
        canonicalPath={MAILING_LIST_CONFIRM_EMAIL_PAGE_URL}
      />

      {/* Page */}
      <div className="grid relative min-h-screen font-poppins bg-colour2 dark:bg-colour2 text-colour1 dark:text-colour1">
        <div className="grid min-h-screen">
          {/* Navigation */}
          <Navbar />

          {/* Header */}
          <header
            className="pt-24 px-8 lg:container lg:mx-auto text-center"
            role="banner"
          >
            <h1 className="text-center font-titleFont text-6xl tracking-widest">
              Confirm Your Email
            </h1>

            <p className="tracking-wider mt-2 text-base xs:text-lg sm:text-xl opacity-90 max-w-2xl mx-auto">
              You're almost done. Confirm your subscription to the {CompanyName}
              mailing list to begin receiving availability updates, tour
              announcements, and exclusive news.
            </p>
          </header>

          {/* Main */}
          <main
            className="grid place-items-center px-6 pt-10 pb-16"
            role="main"
            aria-live="polite"
          >
            <section className="grid gap-4 max-w-lg text-center">
              {status === 'loading' && (
                <>
                  <p className="opacity-90 mb-2">Validating your email…</p>
                  <div className="flex justify-center">
                    <LoadingSpinner lg={true} />
                  </div>
                </>
              )}

              {status === 'success' && (
                <>
                  <p className="text-green-500 font-semibold text-lg">
                    ✓ Your email has been confirmed.
                  </p>
                  <p className="text-sm max-w-md mx-auto">
                    You are now subscribed to updates from {CompanyName}. Expect
                    exclusive session openings, personal announcements, and VIP
                    email-only notices.
                  </p>
                </>
              )}

              {status === 'error' && (
                <>
                  <p className="text-red-500 font-semibold text-lg">
                    ✕ This verification link is invalid or expired.
                  </p>
                  <p className="text-sm max-w-md mx-auto">
                    If your link timed out or was incorrect, you may request
                    another email below.
                  </p>

                  <button
                    onClick={handleResendVerification}
                    disabled={resendStatus === 'loading'}
                    className="text-sm text-blue-600 underline hover:text-blue-800 disabled:opacity-50 mt-2"
                  >
                    {resendStatus === 'loading' ? (
                      <LoadingSpinner xs={true} />
                    ) : (
                      'Resend verification email'
                    )}
                  </button>

                  {resendStatus === 'success' && (
                    <p className="text-green-500 text-sm mt-1">
                      ✓ A new verification email has been sent.
                    </p>
                  )}

                  {resendStatus === 'error' && (
                    <p className="text-red-500 text-sm mt-1">
                      ✕ Unable to resend. Please try again shortly.
                    </p>
                  )}
                </>
              )}
            </section>
          </main>
        </div>

        {/* FAQ JSON-LD */}
        <FAQSchema items={mailingListFaqItems} />
      </div>
    </>
  );
}

export default MailingValidationPage;
